import React, { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import useIntl from '@i18n/useIntl';
import InputController from '@components/form/InputController';
import DropdownController from '@components/form/DropdownController';
import * as businessDetailsConstants from '@features/account-settings/constants';
import * as formConstants from '@components/form/constants';
import { ControllersWrapper } from '@components/form/style';
import { useGetCountriesQuery, useGetStatesQuery } from '@services/address';
import { useSelector } from 'react-redux';
import { RootState } from 'src/shared/state/store';
import isEmpty from 'lodash/isEmpty';
import { ControllerConfigurations } from '@features/account-settings/types';
import { selectCountryToBusinessRegistrationEnabled } from 'src/shared/state/business-registration-number-slice';
import { countryCurrencyTimezoneMap } from 'src/shared/country-data';
import { BUSINESS_NAME_MAX_LENGTH } from 'src/shared/constants';

interface BusinessDetailsControllersProps {
  configuration?: ControllerConfigurations;
}

const BusinessDetailsControllers = ({
  configuration,
}: BusinessDetailsControllersProps) => {
  const { intl } = useIntl();
  const { watch, setValue, unregister, register, getValues } = useFormContext();

  const businessRegistration = useSelector(
    selectCountryToBusinessRegistrationEnabled,
  );
  const isRetailer = useSelector(
    (state: RootState) => state.registration.isRetailer,
  );

  const countryCode = watch(
    businessDetailsConstants.BUSINESS_DETAILS_COUNTRY_FIELD_CONTROLLER_NAME,
  );

  const {
    data: countries,
    isLoading: isLoadingCountryList,
    isError: isLoadingCountryError,
  } = useGetCountriesQuery();

  const {
    data: states,
    isLoading: isLoadingStateList,
    isFetching: isFetchingStateList,
    isError: isLoadingStateError,
  } = useGetStatesQuery(countryCode, {
    skip: !countryCode,
  });

  const state = watch(
    businessDetailsConstants.BUSINESS_DETAILS_STATE_FIELD_CONTROLLER_NAME,
  );
  useEffect(() => {
    if (
      state &&
      states &&
      states.find((validState) => validState.value === state) == undefined
    ) {
      /**
       * When we get a state from the pre-populated address, we are not guaranteed
       * that the state will match one of the valid states returned from AddressService
       * API (for example, "NY" vs "New York"). If this happens, we want to avoid
       * using the invalid state and instead force the user to re-select the valid state.
       */
      setValue(
        businessDetailsConstants.BUSINESS_DETAILS_STATE_FIELD_CONTROLLER_NAME,
        '',
      );
    }
  }, [state, states, setValue]);

  const countryChangeHandler = (countryCode: string) => {
    setValue(
      businessDetailsConstants.BUSINESS_DETAILS_STATE_FIELD_CONTROLLER_NAME,
      '',
    );
    if (
      getValues(
        businessDetailsConstants.ACCOUNT_CURRENCY_FIELD_CONTROLLER_NAME,
      ) === undefined
    ) {
      setValue(
        businessDetailsConstants.ACCOUNT_CURRENCY_FIELD_CONTROLLER_NAME,
        countryCurrencyTimezoneMap.get(countryCode)?.currency,
      );
      setValue(
        businessDetailsConstants.ACCOUNT_TIME_ZONE_FIELD_CONTROLLER_NAME,
        countryCurrencyTimezoneMap.get(countryCode)?.timezone,
      );
    }
  };

  const requiredValidation = useMemo(
    () => ({
      required: intl.getMessage(
        formConstants.REQUIRED_VALIDATION_ERROR_MESSAGE_TRANSLATION_KEY,
      ),
    }),
    [intl],
  );

  const optionalValidationMessage = useMemo(() => undefined, []);

  const requiredValidationMessage = useMemo(
    () =>
      intl.getMessage(
        formConstants.REQUIRED_VALIDATION_ERROR_MESSAGE_TRANSLATION_KEY,
      ),
    [intl],
  );

  const isBusinessRegistrationNumberEnabled = useMemo(() => {
    if (!countryCode) {
      return false;
    }
    return businessRegistration && businessRegistration[countryCode];
  }, [businessRegistration, countryCode]);

  useEffect(() => {
    if (isLoadingStateError) {
      unregister(
        businessDetailsConstants.BUSINESS_DETAILS_STATE_FIELD_CONTROLLER_NAME,
      );
    } else {
      register(
        businessDetailsConstants.BUSINESS_DETAILS_STATE_FIELD_CONTROLLER_NAME,
        { required: true },
      );
    }
  }, [isLoadingStateError, states]);

  return (
    <ControllersWrapper>
      <InputController
        label={intl.getMessage(
          businessDetailsConstants.BUSINESS_DETAILS_NAME_FIELD_LABEL,
        )}
        id={businessDetailsConstants.BUSINESS_DETAILS_NAME_FIELD_ID}
        controllerName={
          businessDetailsConstants.BUSINESS_DETAILS_NAME_FIELD_CONTROLLER_NAME
        }
        registerOptions={{
          ...requiredValidation,
          maxLength: {
            value: BUSINESS_NAME_MAX_LENGTH,
            message: intl.formatMessage(
              formConstants.MAX_LENGTH_BUSINESS_NAME_VALIDATION_ERROR_MESSAGE_TRANSLATION_KEY,
              {
                maxLength: BUSINESS_NAME_MAX_LENGTH.toString(),
              },
            ),
          },
        }}
        helpTipText={intl.getMessage(
          businessDetailsConstants.BUSINESS_DETAILS_NAME_FIELD_TOOLTIP_TRANSLATION_KEY,
        )}
        disabled={
          configuration?.[
            businessDetailsConstants.BUSINESS_DETAILS_NAME_FIELD_CONTROLLER_NAME
          ]?.disabled
        }
      />
      <InputController
        label={intl.getMessage(
          businessDetailsConstants.BUSINESS_DETAILS_PHONE_FIELD_LABEL,
        )}
        id={businessDetailsConstants.BUSINESS_DETAILS_PHONE_FIELD_ID}
        controllerName={
          businessDetailsConstants.BUSINESS_DETAILS_PHONE_FIELD_CONTROLLER_NAME
        }
        registerOptions={{
          ...requiredValidation,
          pattern: {
            value: formConstants.PHONE_VALIDATION,
            message: intl.getMessage(
              formConstants.PHONE_VALIDATION_ERROR_MESSAGE_TRANSLATION_KEY,
            ),
          },
        }}
        helpTipText={intl.getMessage(
          businessDetailsConstants.BUSINESS_DETAILS_PHONE_FIELD_TOOLTIP_TRANSLATION_KEY,
        )}
        type="tel"
      />
      <InputController
        label={intl.getMessage(
          businessDetailsConstants.BUSINESS_DETAILS_WEBSITE_FIELD_LABEL,
        )}
        id={businessDetailsConstants.BUSINESS_DETAILS_WEBSITE_FIELD_ID}
        controllerName={
          businessDetailsConstants.BUSINESS_DETAILS_WEBSITE_FIELD_CONTROLLER_NAME
        }
        registerOptions={{
          ...requiredValidation,
          pattern: {
            value: formConstants.WEBSITE_VALIDATION,
            message: intl.getMessage(
              formConstants.WEBSITE_URL_VALIDATION_ERROR_MESSAGE_TRANSLATION_KEY,
            ),
          },
        }}
        helpTipText={intl.getMessage(
          businessDetailsConstants.BUSINESS_DETAILS_WEBSITE_FIELD_TOOLTIP_TRANSLATION_KEY,
        )}
      />
      <DropdownController
        label={intl.getMessage(
          businessDetailsConstants.BUSINESS_DETAILS_COUNTRY_FIELD_LABEL,
        )}
        id={businessDetailsConstants.BUSINESS_DETAILS_COUNTRY_FIELD_ID}
        controllerName={
          businessDetailsConstants.BUSINESS_DETAILS_COUNTRY_FIELD_CONTROLLER_NAME
        }
        items={countries || []}
        registerOptions={{
          ...requiredValidation,
        }}
        overrideDropdownLabel={intl.getMessage(
          businessDetailsConstants.BUSINESS_DETAILS_COUNTRY_DROPDOWN_PLACEHOLDER,
        )}
        isLoading={isLoadingCountryList}
        disabled={
          isLoadingCountryError ||
          configuration?.[
            businessDetailsConstants
              .BUSINESS_DETAILS_COUNTRY_FIELD_CONTROLLER_NAME
          ]?.disabled
        }
        callback={countryChangeHandler}
      />
      <InputController
        label={intl.getMessage(
          businessDetailsConstants.BUSINESS_DETAILS_ADDRESS_LINE1_FIELD_LABEL,
        )}
        id={businessDetailsConstants.BUSINESS_DETAILS_ADDRESS_LINE1_FIELD_ID}
        controllerName={
          businessDetailsConstants.BUSINESS_DETAILS_ADDRESS_LINE1_FIELD_CONTROLLER_NAME
        }
        registerOptions={{
          ...requiredValidation,
        }}
      />
      <InputController
        label={intl.getMessage(
          businessDetailsConstants.BUSINESS_DETAILS_ADDRESS_LINE2_FIELD_LABEL,
        )}
        id={businessDetailsConstants.BUSINESS_DETAILS_ADDRESS_LINE2_FIELD_ID}
        controllerName={
          businessDetailsConstants.BUSINESS_DETAILS_ADDRESS_LINE2_FIELD_CONTROLLER_NAME
        }
        optionalTag={intl.getMessage(
          formConstants.OPTIONAL_TAG_TRANSLATION_KEY,
        )}
      />
      <InputController
        label={intl.getMessage(
          businessDetailsConstants.BUSINESS_DETAILS_CITY_FIELD_LABEL,
        )}
        id={businessDetailsConstants.BUSINESS_DETAILS_CITY_FIELD_ID}
        controllerName={
          businessDetailsConstants.BUSINESS_DETAILS_CITY_FIELD_CONTROLLER_NAME
        }
        registerOptions={{
          ...requiredValidation,
        }}
      />
      {!isLoadingStateError && !isEmpty(states) ? (
        <DropdownController
          label={intl.getMessage(
            businessDetailsConstants.BUSINESS_DETAILS_STATE_FIELD_LABEL,
          )}
          id={businessDetailsConstants.BUSINESS_DETAILS_STATE_FIELD_ID}
          controllerName={
            businessDetailsConstants.BUSINESS_DETAILS_STATE_FIELD_CONTROLLER_NAME
          }
          items={states || []}
          registerOptions={{
            ...requiredValidation,
          }}
          overrideDropdownLabel={intl.getMessage(
            businessDetailsConstants.BUSINESS_DETAILS_STATE_DROPDOWN_PLACEHOLDER,
          )}
          isLoading={isLoadingStateList || isFetchingStateList}
          disabled={!states}
        />
      ) : (
        <InputController
          label={intl.getMessage(
            businessDetailsConstants.BUSINESS_DETAILS_STATE_FIELD_LABEL,
          )}
          id={businessDetailsConstants.BUSINESS_DETAILS_STATE_FIELD_ID}
          controllerName={
            businessDetailsConstants.BUSINESS_DETAILS_STATE_FIELD_CONTROLLER_NAME
          }
          optionalTag={intl.getMessage(
            formConstants.OPTIONAL_TAG_TRANSLATION_KEY,
          )}
        />
      )}
      <InputController
        label={intl.getMessage(
          businessDetailsConstants.BUSINESS_DETAILS_POSTAL_CODE_FIELD_LABEL,
        )}
        id={businessDetailsConstants.BUSINESS_DETAILS_POSTAL_CODE_FIELD_ID}
        controllerName={
          businessDetailsConstants.BUSINESS_DETAILS_POSTAL_CODE_FIELD_CONTROLLER_NAME
        }
        registerOptions={{
          ...requiredValidation,
          pattern: {
            value: formConstants.POSTAL_CODE_VALIDATION,
            message: intl.getMessage(
              formConstants.NUMBER_VALIDATION_ERROR_MESSAGE_TRANSLATION_KEY,
            ),
          },
        }}
      />

      {!isRetailer && (
        <InputController
          label={intl.getMessage(
            businessDetailsConstants.BUSINESS_DETAILS_REGISTRATION_NUMBER_FIELD_LABEL,
          )}
          id={
            businessDetailsConstants.BUSINESS_DETAILS_REGISTRATION_NUMBER_FIELD_ID
          }
          controllerName={
            businessDetailsConstants.BUSINESS_DETAILS_REGISTRATION_NUMBER_FIELD_CONTROLLER_NAME
          }
          optionalTag={
            isBusinessRegistrationNumberEnabled
              ? ``
              : intl.getMessage(formConstants.OPTIONAL_TAG_TRANSLATION_KEY)
          }
          registerOptions={{
            required: isBusinessRegistrationNumberEnabled
              ? requiredValidationMessage
              : optionalValidationMessage,
          }}
          helpTipText={intl.getMessage(
            businessDetailsConstants.BUSINESS_DETAILS_REGISTRATION_NUMBER_FIELD_TOOLTIP_TRANSLATION_KEY,
          )}
        />
      )}
    </ControllersWrapper>
  );
};

export default BusinessDetailsControllers;
